import { TaskData } from '@components';
import { getPreferredOperator, getPreferredWorksite, setPreferredValues } from '@features/preferredValues';
import {
    Day,
    GetTimesheetsTasksResponse,
    GetUsersResponse,
    TaskState,
    useCreateTimesheetMutation,
    useDeleteTaskMutation,
    useGetTimesheetsReportsMutation,
    useGetTimesheetsTasksMutation,
    useGetUsersMutation
} from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export const usePointageActionPageVM = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const preferredOperator = useSelector(getPreferredOperator);
    const preferredWorksite = useSelector(getPreferredWorksite);

    const [day, setDay] = useState<Day>();
    const [tasks, setTasks] = useState<Day['tasks']>([]);
    const [tasksTypes, setTasksTypes] = useState<GetTimesheetsTasksResponse>([]);
    const [operators, setOperators] = useState<GetUsersResponse['operators']>([]);
    const [isUsageVehiculeChecked, setIsUsageVehiculeChecked] = useState<boolean>(false);
    const [comment, setComment] = useState<string>('');

    const [getUsers, { data: users }] = useGetUsersMutation();

    const [deleteTask] = useDeleteTaskMutation();
    const [getTimesheetsReports, { data: timesheetsReports, isLoading: isTimesheetsReportsLoading, isError: isTimesheetsReportsError }] =
        useGetTimesheetsReportsMutation();

    const [getTimesheetsTasks, { data: timesheetsTasks }] = useGetTimesheetsTasksMutation();

    const [createTimesheet, { isSuccess: isCreateTimesheetSuccess }] = useCreateTimesheetMutation();

    const handleCreateNewTask = () => {
        const newTaskId = Math.max(0, ...tasks.map((t) => Number(t.id) || 0)) + 1;
        setTasks((prev) => [
            ...prev,
            {
                operator_id: preferredOperator ? `${preferredOperator}` : '',
                worksite_id: preferredWorksite ? `${preferredWorksite}` : '',
                hours: '',
                type_id: '',
                isNew: true,
                id: newTaskId
            }
        ]);
    };

    const handleTaskChange = (taskId: string | number, taskData: TaskData) => {
        setTasks((prev) => {
            if (!prev) return prev;

            const updatedTasks = prev.map((task) => {
                if (
                    (typeof taskId === 'string' && task.id === taskId) ||
                    (typeof taskId === 'number' && task.isNew && task.id === taskId)
                ) {
                    const updatedTask = { ...task, ...taskData };
                    if (
                        task.operator_id === updatedTask.operator_id &&
                        task.worksite_id === updatedTask.worksite_id &&
                        task.hours === updatedTask.hours &&
                        task.type_id === updatedTask.type_id
                    ) {
                        return task;
                    }
                    return updatedTask;
                }
                return task;
            });

            const hasChanged = updatedTasks.some((updatedTask, index) => {
                const originalTask = prev[index];
                return (
                    originalTask.operator_id !== updatedTask.operator_id ||
                    originalTask.worksite_id !== updatedTask.worksite_id ||
                    originalTask.hours !== updatedTask.hours ||
                    originalTask.type_id !== updatedTask.type_id
                );
            });

            return hasChanged ? updatedTasks : prev;
        });
    };

    const handleTaskDelete = async (taskId: string | number) => {
        // if (tasks.length === 1) {
        //     setTasks([
        //         {
        //             operator_id: preferredOperator ? `${preferredOperator}` : '',
        //             worksite_id: preferredWorksite ? `${preferredWorksite}` : '',
        //             hours: '',
        //             type_id: '',
        //             isNew: true,
        //             id: 1
        //         }
        //     ]);
        //     return;
        // }

        if (typeof taskId === 'string') {
            try {
                await deleteTask({ task_id: taskId }).unwrap();
                await getTimesheetsReports(undefined);
            } catch (error) {
                console.error('Failed to delete task:', error);
            }
        }

        setTasks((prev) => {
            if (!prev)
                return [
                    {
                        operator_id: preferredOperator ? `${preferredOperator}` : '',
                        worksite_id: preferredWorksite ? `${preferredWorksite}` : '',
                        hours: '',
                        type_id: '',
                        isNew: true,
                        id: 1
                    }
                ];
            return prev.filter((task) => {
                if (typeof taskId === 'string') {
                    return task.id !== taskId;
                }
                return !(task.isNew && task.id === taskId);
            });
        });
    };

    const areTasksFilled = useMemo(() => {
        if (!tasks.length) return false;
        const areTasksValidated = tasks.every((task) => task.state_id === TaskState.VALIDATED);
        if (areTasksValidated) return false;

        return tasks
            .filter((task) => task.state_id !== TaskState.VALIDATED)
            .every((task) => task.operator_id && task.worksite_id && task.hours && task.type_id);
    }, [tasks]);

    const areTasksAlreadyValidated = useMemo(() => {
        return tasks.every((task) => task.state_id === TaskState.VALIDATED);
    }, [tasks]);

    const handleValidate = () => {
        const payload = tasks
            .filter((task) => task.state_id !== TaskState.VALIDATED)
            .map((task) => ({
                ...(task.id && !task.isNew && { id: task.id }),
                operator_id: Number(task.operator_id),
                worksite_id: Number(task.worksite_id),
                hours: Number(task.hours),
                type_id: Number(task.type_id)
            }));

        createTimesheet({
            id: id ?? '',
            personal_car: isUsageVehiculeChecked ? 1 : 0,
            comment,
            tasks: payload
        });
        dispatch(
            setPreferredValues({
                preferredOperator: tasks[tasks.length - 1].operator_id,
                preferredWorksite: tasks[tasks.length - 1].worksite_id
            })
        );
    };

    useEffect(() => {
        getTimesheetsReports(undefined);
        getTimesheetsTasks(undefined);
        getUsers(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isTimesheetsReportsLoading) return;

        if (timesheetsReports) {
            timesheetsReports.forEach((report) => {
                report.days.forEach((day) => {
                    if (day.id === id) {
                        setDay(day);
                        setComment(day.comment);
                        setIsUsageVehiculeChecked(day.personal_car);
                    }
                });
            });
        }
    }, [timesheetsReports, isTimesheetsReportsLoading, isTimesheetsReportsError, id]);

    useEffect(() => {
        if (!timesheetsReports || !day) return;
        if (day && day.tasks && day.tasks.length > 0) {
            setTasks(day.tasks);
        } else {
            setTasks([
                {
                    operator_id: preferredOperator ? `${preferredOperator}` : '',
                    worksite_id: preferredWorksite ? `${preferredWorksite}` : '',
                    hours: '',
                    type_id: '',
                    isNew: true,
                    id: 1
                }
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [day, timesheetsReports]);

    useEffect(() => {
        if (timesheetsTasks) {
            setTasksTypes(timesheetsTasks);
        }
    }, [timesheetsTasks]);

    useEffect(() => {
        if (users) {
            setOperators(users.operators);
        }
    }, [users]);

    useEffect(() => {
        if (isCreateTimesheetSuccess) {
            const weekIndex = searchParams.get('week') || '0';
            const reports = timesheetsReports;

            let foundDay = null;

            if (reports && reports.length > 0) {
                for (const report of reports) {
                    for (const day of report.days) {
                        const isSaturday = new Date(day.date).getDay() === 6;
                        if (day.tasks.length === 0 && !isSaturday && day.id !== id && !day.holidayLabel) {
                            foundDay = day.id;
                            break;
                        }
                    }
                    if (foundDay) break;
                }
            }

            const isUpdate = tasks.some((task) => !task.isNew);

            if (foundDay && !isUpdate) {
                navigate(`/richesses-humaines/pointage/${foundDay}?week=${weekIndex}`, { replace: true, flushSync: true });
                window.location.reload();
            } else {
                navigate(`/richesses-humaines/pointage?week=${weekIndex}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCreateTimesheetSuccess]);

    useEffect(() => {
        if (tasks.length > 0) {
            dispatch(
                setPreferredValues({
                    preferredOperator: tasks[tasks.length - 1].operator_id,
                    preferredWorksite: tasks[tasks.length - 1].worksite_id
                })
            );
        }
    }, [tasks, dispatch]);

    return {
        day,
        tasks,
        tasksTypes,
        operators,
        handleTaskChange,
        handleCreateNewTask,
        handleTaskDelete,
        handleValidate,
        areTasksFilled,
        isUsageVehiculeChecked,
        setIsUsageVehiculeChecked,
        comment,
        setComment,
        areTasksAlreadyValidated
    };
};
